import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV_LINKS, NAV_OFFERINGS_MENU, NAV_TOOLS_MENU } from "@/lib/content";

const OFFERINGS_PATHS = ["/offerings", "/ai-readiness", "/funding-readiness-worksheet"];

function DropLink({ to, label, badge, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center justify-between px-6 py-2.5 text-[13px] transition-colors ${
          isActive
            ? "text-[var(--tmg-gold-soft)]"
            : "text-[var(--tmg-obsidian)] hover:text-[var(--tmg-gold-soft)] hover:bg-white"
        }`
      }
    >
      {label}
      {badge && (
        <span className="bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] text-[9px] font-mono-sm font-bold uppercase tracking-wider px-1.5 py-0.5 ml-3">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const servicesActive = OFFERINGS_PATHS.includes(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSvcOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const linkClass = (isActive) =>
    `text-[13px] font-medium tracking-wide transition-colors ${
      isActive
        ? "text-[var(--tmg-obsidian)] border-b border-[var(--tmg-gold)] pb-1"
        : "text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)]"
    }`;

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[var(--tmg-ivory)]/85 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-20">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="nav-logo"
            className="group flex items-center gap-3"
          >
            <span className="w-9 h-9 bg-[var(--tmg-obsidian)] text-[var(--tmg-gold)] flex items-center justify-center font-display font-bold text-[10px] tracking-tight">
              TMG
            </span>
            <span className="font-display text-base font-semibold tracking-tight whitespace-nowrap">
              TMG Consulting Group
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <NavLink to="/" data-testid="nav-link-home" className={({ isActive }) => linkClass(isActive)}>
              Home
            </NavLink>
            <NavLink to="/about" data-testid="nav-link-about" className={({ isActive }) => linkClass(isActive)}>
              About
            </NavLink>

            {/* Offerings dropdown */}
            <div className="relative group">
              <NavLink
                to="/offerings"
                data-testid="nav-offerings-dropdown"
                className={`text-[13px] font-medium tracking-wide transition-colors flex items-center gap-1.5 ${
                  servicesActive
                    ? "text-[var(--tmg-obsidian)] border-b border-[var(--tmg-gold)] pb-1"
                    : "text-[var(--tmg-muted)] hover:text-[var(--tmg-obsidian)]"
                }`}
              >
                Offerings
                <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" />
              </NavLink>
              <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200">
                <div
                  data-testid="nav-offerings-menu"
                  className="w-72 bg-[var(--tmg-ivory)] border border-[var(--tmg-line)] shadow-[0_24px_48px_-24px_rgba(10,10,10,0.3)] py-2"
                >
                  {NAV_OFFERINGS_MENU.map((l) => (
                    <DropLink
                      key={l.to + l.label}
                      to={l.to}
                      label={l.label}
                      testId={`nav-dropdown-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    />
                  ))}
                  <div className="my-2 mx-6 border-t border-[var(--tmg-line)]" />
                  <p className="px-6 pb-1 tmg-overline text-[9px]">Free Tools</p>
                  {NAV_TOOLS_MENU.map((l) => (
                    <DropLink
                      key={l.to}
                      to={l.to}
                      label={l.label}
                      badge={l.badge}
                      testId={`nav-dropdown-${l.to.replace(/\//g, "")}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <NavLink to="/contact" data-testid="nav-link-contact" className={({ isActive }) => linkClass(isActive)}>
              Contact
            </NavLink>
          </nav>

          <div className="hidden lg:flex items-center">
            <Link
              to="/book-call"
              data-testid="nav-book-call-btn"
              className="tmg-btn-dark text-xs px-5 py-3 whitespace-nowrap"
            >
              Book a Discovery Call <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[var(--tmg-ivory)] border-t border-black/5">
          <div className="px-6 py-6 flex flex-col gap-1">
            <NavLink
              to="/"
              data-testid="mobile-nav-home"
              className={({ isActive }) => `py-3 text-base ${isActive ? "text-[var(--tmg-gold-soft)]" : "text-[var(--tmg-obsidian)]"}`}
            >
              Home
            </NavLink>
            <NavLink
              to="/about"
              data-testid="mobile-nav-about"
              className={({ isActive }) => `py-3 text-base ${isActive ? "text-[var(--tmg-gold-soft)]" : "text-[var(--tmg-obsidian)]"}`}
            >
              About
            </NavLink>

            <button
              data-testid="mobile-offerings-toggle"
              onClick={() => setSvcOpen(!svcOpen)}
              className={`py-3 text-base flex items-center justify-between w-full text-left ${
                servicesActive ? "text-[var(--tmg-gold-soft)]" : "text-[var(--tmg-obsidian)]"
              }`}
            >
              Offerings
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${svcOpen ? "rotate-180" : ""}`} />
            </button>
            {svcOpen && (
              <div data-testid="mobile-offerings-menu" className="flex flex-col border-l border-[var(--tmg-line)] ml-1 mb-2">
                {[...NAV_OFFERINGS_MENU, ...NAV_TOOLS_MENU].map((l) => (
                  <NavLink
                    key={l.to + l.label}
                    to={l.to}
                    data-testid={`mobile-nav-${l.to.replace(/\//g, "")}`}
                    className={({ isActive }) =>
                      `py-2.5 pl-5 text-sm flex items-center gap-2 ${
                        isActive ? "text-[var(--tmg-gold-soft)]" : "text-[var(--tmg-muted)]"
                      }`
                    }
                  >
                    {l.label}
                    {l.badge && (
                      <span className="bg-[var(--tmg-gold)] text-[var(--tmg-obsidian)] text-[9px] font-mono-sm font-bold uppercase tracking-wider px-1.5 py-0.5">
                        {l.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}

            <NavLink
              to="/contact"
              data-testid="mobile-nav-contact"
              className={({ isActive }) => `py-3 text-base ${isActive ? "text-[var(--tmg-gold-soft)]" : "text-[var(--tmg-obsidian)]"}`}
            >
              Contact
            </NavLink>
            <Link
              to="/book-call"
              data-testid="mobile-book-call-btn"
              className="tmg-btn-dark mt-3 w-full justify-center"
            >
              Book a Discovery Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
